import { Icon } from '@capsico/ui';
import type { AddressView, Town } from '@capsico/contracts';
import { Sheet } from './ui';

/** Pattern S/T location chooser: GPS, saved addresses, add new, or a town. Presentation only. */
export function LocationSheet({
  title,
  labels,
  gps,
  onUseCurrent,
  addresses,
  addressTitle,
  addressLine,
  onChooseAddress,
  signedIn,
  onAddAddress,
  onSignIn,
  towns,
  onChooseTown,
  onClose,
}: {
  title: string;
  labels: {
    useCurrent: string;
    locating: string;
    denied: string;
    unavailable: string;
    saved: string;
    add: string;
    signInToSave: string;
    towns: string;
    notServed: string;
    default: string;
  };
  gps: 'idle' | 'locating' | 'denied' | 'unavailable';
  onUseCurrent: () => void;
  addresses: AddressView[];
  addressTitle: (address: AddressView) => string;
  addressLine: (address: AddressView) => string;
  onChooseAddress: (address: AddressView) => void;
  signedIn: boolean;
  onAddAddress: () => void;
  onSignIn: () => void;
  towns: Town[];
  onChooseTown: (town: Town) => void;
  onClose: () => void;
}) {
  return (
    <Sheet title={title} onClose={onClose}>
      <button
        type="button"
        onClick={onUseCurrent}
        disabled={gps === 'locating'}
        className="flex w-full items-center gap-3 rounded-xl bg-surface px-3 py-3 text-left shadow-[inset_0_0_0_1px_var(--color-hair)] active:bg-tile"
      >
        <span className="inline-flex size-9 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
          <Icon name="pin" size={18} />
        </span>
        <span className="flex-1 text-sm font-semibold text-brand-ink">
          {gps === 'locating' ? labels.locating : labels.useCurrent}
        </span>
        <Icon name="chevron-right" size={16} />
      </button>
      {(gps === 'denied' || gps === 'unavailable') && (
        <p className="mt-2 text-xs text-negative">
          {gps === 'denied' ? labels.denied : labels.unavailable}
        </p>
      )}

      <p className="mt-5 mb-2 text-micro font-semibold tracking-[0.06em] text-ink-3 uppercase">
        {labels.saved}
      </p>
      {signedIn ? (
        <div className="divide-y divide-hair overflow-hidden rounded-xl shadow-[inset_0_0_0_1px_var(--color-hair)]">
          {addresses.map((address) => (
            <button
              key={address.id}
              type="button"
              onClick={() => onChooseAddress(address)}
              className="flex w-full items-start gap-3 px-3 py-3 text-left active:bg-tile"
            >
              <Icon
                name={
                  address.label === 'work' ? 'store' : address.label === 'home' ? 'home' : 'pin'
                }
                size={18}
              />
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-ink">
                  {addressTitle(address)}
                  {address.isDefault && (
                    <span className="rounded bg-brand-soft px-1.5 text-micro font-semibold text-brand-ink">
                      {labels.default}
                    </span>
                  )}
                </span>
                <span className="block truncate text-xs text-ink-2">{addressLine(address)}</span>
                {!address.serviceable && (
                  <span className="block text-caption text-negative">{labels.notServed}</span>
                )}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={onAddAddress}
            className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-tile"
          >
            <Icon name="plus" size={18} />
            <span className="text-sm font-semibold text-brand-ink">{labels.add}</span>
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onSignIn}
          className="w-full rounded-xl px-3 py-3 text-left text-sm font-semibold text-brand-ink shadow-[inset_0_0_0_1px_var(--color-hair)]"
        >
          {labels.signInToSave}
        </button>
      )}

      {towns.length > 0 && (
        <>
          <p className="mt-5 mb-2 text-micro font-semibold tracking-[0.06em] text-ink-3 uppercase">
            {labels.towns}
          </p>
          <div className="flex flex-wrap gap-2">
            {towns.map((town) => (
              <button
                key={town.id}
                type="button"
                onClick={() => onChooseTown(town)}
                className="h-8 rounded-full px-3.5 text-xs text-ink shadow-[inset_0_0_0_1px_var(--color-control)] active:bg-tile"
              >
                {town.name}
              </button>
            ))}
          </div>
        </>
      )}
    </Sheet>
  );
}
